import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Calculator,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  BadgeInfo,
  Coins,
  FileText,
  Lightbulb,
  Sparkles,
  TrendingUp,
} from 'lucide-react';

import {
  ateeOptionValue,
  type AteeFiche,
  type AteeInput,
} from '@shared/schemas/atee-fiche';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { cn } from '../../lib/cn';

/* ─────────────────────────────────────── Types API ─────────────────────────────────────── */

interface CalcStep {
  n: number;
  label: string;
  formula: string;
  result: number;
}

interface CalcResult {
  ficheCode: string;
  ficheTitre: string;
  formuleType: string;
  unite: string;
  steps: CalcStep[];
  kwhCumacBrut: number;
  kwhCumacBonifie: number;
  mwhCumac: number;
  primeEur: number;
  warnings: string[];
}

interface CalcError {
  error: string;
  code: string;
  details?: Record<string, unknown> | null;
}

/* ─────────────────────────────────────── Props ─────────────────────────────────────── */

interface CeeCalculatorProps {
  /** Code de la fiche (utilisé pour l'API). */
  ficheCode: string;
  /** Bloc calculatrice ATEE complet. */
  calculatrice: AteeFiche['calculatrice'];
  className?: string;
}

/* ─────────────────────────────────────── Helpers ─────────────────────────────────────── */

function initialValuesFor(inputs: AteeInput[]): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const def of inputs) {
    if (def.default !== undefined) {
      out[def.id] = typeof def.default === 'boolean' ? (def.default ? 1 : 0) : (def.default as string | number);
      continue;
    }
    if (def.type === 'number') {
      // Valeur médiane raisonnable.
      const min = def.min ?? 1;
      const max = def.max ?? 10000;
      if (def.id.includes('surface') || def.id === 'S') out[def.id] = 100;
      else if (def.id === 'prix_mwh') out[def.id] = 8;
      else if (def.id.startsWith('nb_')) out[def.id] = 1;
      else out[def.id] = Math.min(Math.max(min, 1), max);
    } else if (def.type === 'select' || def.type === 'radio') {
      const first = (def.options ?? [])[0];
      if (first) {
        const v = ateeOptionValue(first);
        if (v !== undefined) out[def.id] = String(v);
      }
    }
  }
  // Defaults usuels.
  if (out.zone_geo === undefined) out.zone_geo = 'metropole';
  if (out.precarite === undefined) out.precarite = 'standard';
  if (out.prix_mwh === undefined) out.prix_mwh = 8;
  return out;
}

function formatEur(n: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function formatNumber(n: number, digits = 0): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: digits }).format(n);
}

/* ─────────────────────────────────────── Composant ─────────────────────────────────────── */

export function CeeCalculator({ ficheCode, calculatrice, className }: CeeCalculatorProps) {
  // Inputs définis par la fiche ATEE.
  const inputs = useMemo(() => calculatrice.inputs ?? [], [calculatrice.inputs]);

  // Fiche non calculable : fallback CTA.
  const notCalculable = calculatrice.calculable === false;

  const [values, setValues] = useState<Record<string, string | number>>(() => initialValuesFor(inputs));
  const [result, setResult] = useState<CalcResult | null>(null);
  const [errorState, setErrorState] = useState<CalcError | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstCalcRef = useRef(true);

  const performCalculation = useCallback(
    async (currentValues: Record<string, string | number>) => {
      setLoading(true);
      setErrorState(null);
      try {
        const response = await fetch(`/api/cee/fiches/${ficheCode}/calculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inputs: currentValues }),
        });
        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as CalcError | null;
          setErrorState(body ?? { error: `Erreur ${response.status}`, code: 'HTTP_ERROR' });
          setResult(null);
        } else {
          const body = (await response.json()) as CalcResult;
          setResult(body);
        }
      } catch (err) {
        setErrorState({ error: err instanceof Error ? err.message : 'Erreur réseau', code: 'NETWORK_ERROR' });
        setResult(null);
      } finally {
        setLoading(false);
      }
    },
    [ficheCode],
  );

  // Auto-calcul : immédiat au 1er mount, debounced ensuite.
  useEffect(() => {
    if (notCalculable) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = isFirstCalcRef.current ? 0 : 400;
    debounceRef.current = setTimeout(() => {
      isFirstCalcRef.current = false;
      void performCalculation(values);
    }, delay);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [values, performCalculation, notCalculable]);

  const handleChange = (id: string, value: string | number) => {
    setValues((prev) => ({ ...prev, [id]: value }));
  };

  // Fiche non calculable → fallback.
  if (notCalculable) {
    return (
      <section className={cn('relative', className)}>
        <h2
          id="simulateur-cee"
          className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"
        >
          <Calculator className="h-5 w-5 text-stone-400" />
          Simulateur de prime
        </h2>
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
          <div className="flex items-start gap-3">
            <FileText className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="space-y-2">
              <p className="font-semibold text-amber-900">
                Cette fiche nécessite une étude personnalisée
              </p>
              <p className="text-sm text-amber-800">
                {calculatrice.raison_non_calculable ??
                  'Le calcul de cette prime CEE requiert une fiche de calcul officielle remplie par un bureau d\'études.'}
              </p>
              <Button asChild className="mt-2">
                <a href="/contact">Demander un devis personnalisé</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn('relative', className)}>
      <h2
        id="simulateur-cee"
        className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"
      >
        <Calculator className="h-5 w-5 text-brand-600" />
        Simulateur de prime
        <span className="ml-auto text-xs font-normal text-stone-400">
          Calcul officiel ATEE — Formule {calculatrice.formule_type}
        </span>
      </h2>

      <div className="overflow-hidden rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50/60 to-paper shadow-subtle">
        {/* Formulaire d'inputs */}
        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
          {inputs.map((def) => (
            <CalcInputField
              key={def.id}
              def={def}
              value={values[def.id] ?? ''}
              onChange={(v) => handleChange(def.id, v)}
            />
          ))}
        </div>

        {/* Séparateur */}
        <div className="border-t border-brand-100 bg-white/60 px-5 py-4">
          {errorState ? (
            <ErrorBanner error={errorState} />
          ) : result ? (
            <ResultDisplay result={result} loading={loading} />
          ) : (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-stone-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Calcul en cours…
            </div>
          )}
        </div>

        {/* Note pied de page */}
        <div className="border-t border-stone-200 bg-stone-50/80 px-5 py-3 text-[11px] leading-relaxed text-stone-500">
          Estimation indicative basée sur les formules officielles ATEE (catalogue des fiches CEE).
          Le montant exact dépend du prix négocié avec l'obligé et de la conformité de votre dossier.
        </div>
      </div>

      {/* Contexte marché + tips */}
      {result && <CeeMarketContext result={result} ficheCode={ficheCode} />}
    </section>
  );
}

/* ─────────────────────────────────────── Champs ─────────────────────────────────────── */

function CalcInputField({
  def,
  value,
  onChange,
}: {
  def: AteeInput;
  value: string | number;
  onChange: (v: string | number) => void;
}) {
  if (def.type === 'select' || def.type === 'radio') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={def.id} className="text-stone-700">
          {def.label}
        </Label>
        <Select value={String(value)} onValueChange={(v) => onChange(v)}>
          <SelectTrigger id={def.id} className="bg-white">
            <SelectValue placeholder="Sélectionner…" />
          </SelectTrigger>
          <SelectContent>
            {(def.options ?? []).map((opt) => {
              const v = ateeOptionValue(opt);
              if (v === undefined) return null;
              return (
                <SelectItem key={String(v)} value={String(v)}>
                  {opt.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {def.aide && <p className="text-[11px] leading-relaxed text-stone-500">{def.aide}</p>}
      </div>
    );
  }
  // Number / autre
  return (
    <div className="space-y-1.5">
      <Label htmlFor={def.id} className="text-stone-700">
        {def.label}
        {def.requis && <span className="ml-1 text-brand-600">*</span>}
      </Label>
      <Input
        id={def.id}
        type="number"
        inputMode="decimal"
        min={def.min}
        max={def.max}
        value={String(value)}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? '' : Number(v));
        }}
        className="bg-white"
        placeholder={def.placeholder}
      />
      {def.aide && <p className="text-[11px] leading-relaxed text-stone-500">{def.aide}</p>}
    </div>
  );
}

/* ─────────────────────────────────────── Résultat ─────────────────────────────────────── */

function ResultDisplay({ result, loading }: { result: CalcResult; loading: boolean }) {
  return (
    <div className="space-y-4">
      {/* Prime en gros */}
      <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Prime CEE estimée
          </p>
          <p className="mt-1 flex items-baseline gap-2">
            <span
              className={cn(
                'font-mono text-4xl font-bold tabular-nums text-brand-800 transition-opacity duration-200',
                loading && 'opacity-50',
              )}
            >
              {formatEur(Math.max(0, Math.round(result.primeEur)))}
            </span>
            <Coins className="h-5 w-5 text-brand-500" />
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-right sm:gap-4">
          <Stat label="kWh cumac" value={formatNumber(result.kwhCumacBonifie)} loading={loading} />
          <Stat label="MWh cumac" value={formatNumber(result.mwhCumac, 1)} loading={loading} />
        </div>
      </div>

      {/* Étapes pédagogiques */}
      <details className="group rounded-lg border border-stone-200 bg-stone-50/60 px-4 py-3 open:bg-stone-50">
        <summary className="cursor-pointer list-none">
          <div className="flex items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm font-medium text-ink">
              <Sparkles className="h-4 w-4 text-brand-500" />
              Voir le détail du calcul ({result.steps.length} étapes)
            </p>
            <span className="text-xs text-stone-400 group-open:rotate-90 transition-transform">›</span>
          </div>
        </summary>
        <ol className="mt-3 space-y-2 text-sm">
          {result.steps.map((step) => (
            <li
              key={step.n}
              className="flex flex-col gap-1 rounded-md border border-stone-200 bg-white px-3 py-2 sm:flex-row sm:items-baseline sm:justify-between"
            >
              <span className="font-medium text-stone-700">
                <span className="mr-2 inline-flex h-5 min-w-5 items-center justify-center rounded bg-brand-100 px-1 font-mono text-[10px] font-bold text-brand-800">
                  {step.n}
                </span>
                {step.label}
              </span>
              <span className="text-right">
                <span className="font-mono text-xs text-stone-500">{step.formula}</span>
                <span className="ml-2 font-mono font-bold tabular-nums text-ink">
                  = {formatNumber(step.result, step.n >= 3 ? 2 : 0)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </details>

      {/* Warnings */}
      {result.warnings.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-800">
          <p className="mb-1 flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="h-3.5 w-3.5" />
            Avertissements
          </p>
          <ul className="space-y-1">
            {result.warnings.map((w, i) => (
              <li key={i}>• {w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, loading }: { label: string; value: string; loading: boolean }) {
  return (
    <div className="flex flex-col items-end">
      <p className="text-[10px] font-medium uppercase tracking-wider text-stone-400">{label}</p>
      <p
        className={cn(
          'font-mono text-sm font-semibold tabular-nums text-stone-800 transition-opacity duration-200',
          loading && 'opacity-50',
        )}
      >
        {value}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────── Erreurs ─────────────────────────────────────── */

function ErrorBanner({ error }: { error: CalcError }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50/80 p-3 text-sm text-red-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
      <div className="space-y-1">
        <p className="font-semibold">Impossible de calculer la prime</p>
        <p className="text-xs leading-relaxed text-red-700">{error.error}</p>
        {error.code === 'INVALID_INPUT' && (
          <p className="text-xs text-red-600">Vérifiez les champs saisis ci-dessus.</p>
        )}
        {(error.code === 'MISSING_LOOKUP' || error.code === 'MISSING_VARIABLE') && (
          <p className="text-xs text-red-600">
            Cette fiche nécessite une étude manuelle.{' '}
            <a href="/contact" className="font-semibold underline">
              Obtenir un devis personnalisé
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────── Contexte marché + tips ─────────────────────────────────────── */

/**
 * Prix marché observés pour les MWh cumac (€/MWh cumac).
 * Source : Observatoire CEE, ATEE — valeurs moyennes 2025-2026.
 * Les prix varient selon la taille du gisement, la qualité du dossier,
 * la notoriété de l'obligé et la saisonnalité.
 */
const MARKET_PRICE_MWHCUMAC = {
  low: 5.5,
  median: 7.5,
  high: 10.0,
} as const;

/**
 * Tips d'optimisation de la prime CEE par famille de fiches.
 */
const FICHE_TIPS: Record<string, string[]> = {
  // Résidentiel — isolation
  'BAR-EN': [
    "Vérifiez l'éligibilité « ménages modestes » pour bénéficier du tarif bonifiée (×1,5).",
    "Regroupez plusieurs gestes en un seul dossier : la prime cumulée peut dépasser la somme des primes individuelles.",
    "Un obligé spécialisé en résidentiel propose généralement un prix MWh cumac 15–25 % supérieur à la moyenne.",
  ],
  // Résidentiel — chauffage
  'BAR-TH': [
    "Le remplacement d'un système fossile existant ouvre droit au Coup de Pouce — bonus de 40 à 100 % selon l'équipement.",
    "Négociez directement avec plusieurs obligés : les prix varient de 5,50 à 10 €/MWh cumac pour un même gisement.",
    "Un MAR (Mon Accompagnateur Rénov') obligatoire pour le parcours accompagné peut aussi piloter la valorisation CEE.",
  ],
  // Industrie
  'IND-': [
    "Les fiches industrie ont des gisements importants : la négociation du prix est cruciale (>10 €/MWh cumac possible).",
    "Faites auditer votre installation par un bureau d'études avant dépôt pour maximiser les MWh cumac valorisables.",
    "Vérifiez si votre secteur d'activité (NAF) ouvre droit à la CSPE et aux bonifications sectorielles.",
  ],
  // Tertiaire
  'BAT-': [
    "En tertiaire, regroupez tous vos sites en un seul dossier pour atteindre un gisement attractif (> 1 GWh cumac).",
    "Les démarches collectives via un syndic ou une foncière permettent de négocier à un meilleur prix.",
    "Un audit énergétique préalable peut identifier des gisements complémentaires non encore valorisés.",
  ],
  // Agriculture
  'AGRI-': [
    "Les CUMA (coopératives d'utilisation de matériel agricole) peuvent mutualiser les dossiers et négocier à meilleur prix.",
    "Vérifiez les fiches spécifiques serres, séchoirs et élevage — les gisements sont souvent sous-exploités.",
  ],
  // Transport
  'TRA-': [
    "Pour les flottes, consolidez tous vos véhicules sur un seul dossier : les obligés offrent de meilleures conditions.",
    "L'électrification partielle d'une flotte peut être combinée avec des fiches d'infrastructure de recharge.",
  ],
};

/** Retourne les tips pertinents pour un code fiche donné */
function getTipsForFiche(ficheCode: string): string[] {
  for (const [prefix, tips] of Object.entries(FICHE_TIPS)) {
    if (ficheCode.toUpperCase().startsWith(prefix)) return tips;
  }
  return [
    "Mettez les obligés en concurrence : les prix peuvent varier de 30 à 50 % pour un même dossier.",
    "Vérifiez les bonifications ménages modestes et Coup de Pouce qui peuvent s'appliquer à votre opération.",
    "Un courtier CEE spécialisé peut vous aider à valoriser votre gisement au meilleur prix.",
  ];
}

/** Estime la prime au prix médian du marché */
function estimateMarketPrime(mwhCumac: number): { low: number; median: number; high: number } {
  return {
    low: Math.round(mwhCumac * MARKET_PRICE_MWHCUMAC.low),
    median: Math.round(mwhCumac * MARKET_PRICE_MWHCUMAC.median),
    high: Math.round(mwhCumac * MARKET_PRICE_MWHCUMAC.high),
  };
}

function CeeMarketContext({ result, ficheCode }: { result: CalcResult; ficheCode: string }) {
  const market = estimateMarketPrime(result.mwhCumac);
  const tips = getTipsForFiche(ficheCode);
  const primeSimulée = Math.max(0, Math.round(result.primeEur));

  // Niveau de prix de la prime simulée par rapport au marché
  const marketLevel =
    primeSimulée >= market.high * 0.95
      ? 'high'
      : primeSimulée >= market.median * 0.9
        ? 'median'
        : 'low';

  return (
    <div className="mt-6 space-y-4">
      {/* Fourchette marché */}
      <div className="rounded-2xl border border-brand-100 bg-brand-50/40 p-4">
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-brand-600" strokeWidth={1.7} />
          <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-stone-500">
            Fourchette de prix marché
          </p>
        </div>
        <div className="flex items-stretch gap-2">
          {([
            { label: 'Prix bas', value: market.low, color: 'bg-stone-200 text-stone-700' },
            { label: 'Prix médian', value: market.median, color: 'bg-brand-200 text-brand-800', highlight: true },
            { label: 'Prix haut', value: market.high, color: 'bg-accent-200 text-accent-800' },
          ] as const).map((tier) => (
            <div
              key={tier.label}
              className={cn(
                'flex flex-1 flex-col items-center rounded-xl px-2 py-3',
                'highlight' in tier && tier.highlight ? 'ring-2 ring-brand-400' : '',
                tier.color,
              )}
            >
              <p className="text-[10px] font-bold uppercase tracking-wide opacity-70">
                {tier.label}
              </p>
              <p className="mt-1 font-mono text-[18px] font-black tabular-nums">
                {formatEur(tier.value)}
              </p>
              <p className="text-[9px] font-semibold opacity-60">
                {tier.label === 'Prix bas' ? `${MARKET_PRICE_MWHCUMAC.low} €/MWh c` :
                 tier.label === 'Prix médian' ? `${MARKET_PRICE_MWHCUMAC.median} €/MWh c` :
                 `${MARKET_PRICE_MWHCUMAC.high} €/MWh c`}
              </p>
            </div>
          ))}
        </div>

        {/* Positionnement de la prime simulée */}
        {primeSimulée > 0 && (
          <div className={cn(
            'mt-3 flex items-start gap-2 rounded-lg p-3 text-[11.5px]',
            marketLevel === 'high'
              ? 'bg-accent-100 text-accent-900'
              : marketLevel === 'median'
                ? 'bg-brand-100 text-brand-900'
                : 'bg-stone-100 text-stone-700',
          )}>
            <BadgeInfo className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.7} />
            <p>
              {marketLevel === 'high' && (
                <>La prime simulée de <strong>{formatEur(primeSimulée)}</strong> est dans le haut de la fourchette marché. Excellent potentiel de négociation.</>
              )}
              {marketLevel === 'median' && (
                <>La prime simulée de <strong>{formatEur(primeSimulée)}</strong> est dans la médiane marché. Une mise en concurrence d'obligés peut améliorer ce montant.</>
              )}
              {marketLevel === 'low' && (
                <>La prime simulée de <strong>{formatEur(primeSimulée)}</strong> est en dessous de la médiane. Vérifiez les bonifications applicables ou consultez un courtier CEE.</>
              )}
            </p>
          </div>
        )}

        <p className="mt-3 text-[10.5px] leading-relaxed text-stone-400">
          Prix marché observés pour {formatNumber(result.mwhCumac, 1)} MWh cumac (source : ATEE / observatoire des prix CEE 2025-2026).
          Le montant final dépend de la négociation avec l'obligé.
        </p>
      </div>

      {/* Tips d'optimisation */}
      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-accent-600" strokeWidth={1.7} />
          <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-stone-500">
            Conseils pour maximiser votre prime
          </p>
        </div>
        <ul className="space-y-2.5">
          {tips.map((tip, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-500" strokeWidth={2.2} />
              <p className="text-[12px] leading-relaxed text-stone-700">{tip}</p>
            </li>
          ))}
        </ul>
        <div className="mt-4 border-t border-stone-100 pt-3">
          <Button asChild variant="brand" size="sm" className="w-full gap-2">
            <a href="/contact?source=cee-fiche">
              <Sparkles className="h-3.5 w-3.5" />
              Obtenir une valorisation personnalisée
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
